import React, { useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Spinner } from '@fos/eniram-web-common-ui/dist/components/Spinner';

import { searchState } from '@modules/core/store/SearchState';
import { Header, NavBar } from '@modules/core/components';
import { NavigationTypes } from '@modules/core/constants';
import { PreviousLocationProvider } from '@modules/core/providers';
import { Toaster } from '@modules/shared/components';
import { NavigationItem, RoleType } from '@modules/core/models';
import { PreviousLocationDiscarded, PreviousLocationDepth } from '@modules/shared/constants';
import { useStore } from '@store/StoreEffect';

const AboutPage = lazy(() => import('@pages/about-page/AboutPage'));
const ErrorPage = lazy(() => import('@pages/error-page/ErrorPage'));
const RolesPage = lazy(() => import('@pages/roles-page/RolesPage'));
const FleetPage = lazy(() => import('@pages/fleet-page/FleetPage'));
const ProductPage = lazy(() => import('@pages/product-page/ProductPage'));
const OrganizationsPage = lazy(() => import('@pages/organizations-page/OrganizationsPage'));
const OrganizationsDetailsPage = lazy(() => import('@pages/organizations-page/details/OrganizationsDetailsPage'));
const VesselDetailsPage = lazy(() => import('@pages/vessels-page/details/VesselDetailsPage'));
const ApiClientsPage = lazy(() => import('@pages/api-clients/ApiClientsPage'));
const ApiClientsDetailsPage = lazy(() => import('@pages/api-clients/details/ApiClientsDetailsPage'));
const AccountsPage = lazy(() => import('@pages/accounts-page/AccountsPage'));
const LoadingFallback = () => <Spinner />;

export const AppRouter = observer(() => {
    const {
        userStore,
        vesselGroupsStoreAll,
        fleetStoreVesselGroups,
        dataSourceStore,
        accountsStoreAll,
        rolesStoreAll,
        fleetStoreAll,
        connectedProductsStoreAll,
        vesselStore,
        vesselSharesStore,
        apiClientsStoreAll
    } = useStore();

    const ownerOrganization = userStore.isSuperOrSupportUser ? null : userStore.currentUser.organization;
    const isMainSuperuserFleetPage = userStore.isSuperOrSupportUser;

    const navItems = useMemo(() => {
        const items: NavigationItem[] = [];

        if (userStore.isAccessable([RoleType.Superuser, RoleType.Support], false)) {
            items.push({
                title: 'Organizations',
                link: 'organizations'
            });
        }

        items.push(
            ...[
                {
                    title: 'Users',
                    link: 'users'
                },
                {
                    title: 'Fleet',
                    link: 'fleet'
                }
            ]
        );

        if (
            userStore.isAccessable([RoleType.Admin], false) &&
            !userStore.isAccessable([RoleType.Superuser, RoleType.Support], false)
        ) {
            items.push(
                ...[
                    {
                        title: 'Products',
                        link: 'products'
                    },
                    {
                        title: 'Roles',
                        link: 'roles'
                    }
                ]
            );
        }

        if (userStore.canViewApiClients) {
            items.push({
                title: 'API Clients',
                link: 'api-clients'
            });
        }

        return items;
    }, [userStore.roles, userStore.permissions, userStore.canViewApiClients]);

    return (
        <main>
            <Router>
                <PreviousLocationProvider depth={PreviousLocationDepth} discardLocations={PreviousLocationDiscarded}>
                    <header>
                        <Header user={userStore.currentUser} userStore={userStore} />
                    </header>
                    <Toaster toasterId="global" />
                    <Suspense fallback={<LoadingFallback />}>
                        <Switch>
                            <Route path="/about">
                                <AboutPage />
                            </Route>
                            <Route path="/">
                                <section>
                                    <nav>
                                        <NavBar
                                            match={false}
                                            navItems={navItems}
                                            navType={NavigationTypes.Left}
                                            onChange={searchState.clearSearches}
                                        />
                                    </nav>
                                    <section className="router-outlet" qa-id="tcms-app-page">
                                        <Suspense fallback={<LoadingFallback />}>
                                            <Switch>
                                                <Route path="/products">
                                                    <ProductPage />
                                                </Route>
                                                <Route
                                                    path="/organizations/:organizationId"
                                                    render={() =>
                                                        userStore.isAccessable(
                                                            [RoleType.Superuser, RoleType.Support],
                                                            false
                                                        ) && <OrganizationsDetailsPage />
                                                    }
                                                />
                                                <Route
                                                    path="/organizations"
                                                    render={() =>
                                                        userStore.isAccessable(
                                                            [RoleType.Superuser, RoleType.Support],
                                                            false
                                                        ) && <OrganizationsPage />
                                                    }
                                                />
                                                <Route path="/roles">
                                                    <RolesPage
                                                        rolesStore={rolesStoreAll}
                                                        accountsStore={accountsStoreAll}
                                                    />
                                                </Route>
                                                <Route path="/users">
                                                    <AccountsPage
                                                        rolesStore={rolesStoreAll}
                                                        accountsStore={accountsStoreAll}
                                                    />
                                                </Route>
                                                <Route path="/fleet/vessels/:vesselId">
                                                    <VesselDetailsPage
                                                        vesselStore={vesselStore}
                                                        vesselSharesStore={vesselSharesStore}
                                                        connectedProductsStore={connectedProductsStoreAll}
                                                        dataSourceStore={dataSourceStore}
                                                    />
                                                </Route>
                                                <Route path="/fleet">
                                                    <FleetPage
                                                        accessRoles={[RoleType.Admin]}
                                                        connectedProductsStore={connectedProductsStoreAll}
                                                        dataSourceStore={dataSourceStore}
                                                        isShowGroupsFilter={!isMainSuperuserFleetPage}
                                                        fleetStore={
                                                            isMainSuperuserFleetPage
                                                                ? fleetStoreAll
                                                                : fleetStoreVesselGroups
                                                        }
                                                        {...(!isMainSuperuserFleetPage && {
                                                            filterVesselsGroup: fleetStoreAll.filterVessels,
                                                            modalFilterFunction: fleetStoreVesselGroups.filterVessels,
                                                            ownerOrganization: ownerOrganization,
                                                            vesselGroupStore: vesselGroupsStoreAll
                                                        })}
                                                    />
                                                </Route>
                                                {userStore.canViewApiClients && (
                                                    <Route path="/api-clients/:apiClientId">
                                                        <ApiClientsDetailsPage />
                                                    </Route>
                                                )}
                                                {userStore.canViewApiClients && (
                                                    <Route path="/api-clients">
                                                        <ApiClientsPage apiClientsStore={apiClientsStoreAll} />
                                                    </Route>
                                                )}
                                                <Route
                                                    exact
                                                    path="/"
                                                    render={() => {
                                                        return userStore.isSuperOrSupportUser ? (
                                                            <Redirect to="/organizations" />
                                                        ) : (
                                                            <Redirect to="/users" />
                                                        );
                                                    }}
                                                />
                                                <Route component={ErrorPage} />
                                            </Switch>
                                        </Suspense>
                                    </section>
                                </section>
                            </Route>
                        </Switch>
                    </Suspense>
                </PreviousLocationProvider>
            </Router>
        </main>
    );
});
